import React, { useState, useContext } from 'react';
import { Card, Typography, Button, Alert, Chip } from '@material-tailwind/react';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';
import { useJobCardStages } from '../../../../context/JobCardStagesContext';
import { rootUrl } from '../../../../config/config';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools, faCheckCircle, faLock, faBan, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';

export default function ChargeSummaryCard({ jobCard, charges, onStatusUpdate }) {
    const { user } = useContext(AuthContext);
    const { stages, loading: stagesLoading, error: stagesError } = useJobCardStages();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const jobCardNumber = jobCard?.job_card_no || 'N/A';
    const currentStatus = jobCard?.status || 'OPEN';

    // Use dynamic stages from context - case insensitive comparison
    const currentStageIndex = stages.findIndex(stage => stage.code.toUpperCase() === currentStatus.toUpperCase());
    const nextStage = currentStageIndex >= 0 && currentStageIndex < stages.length - 1
        ? stages[currentStageIndex + 1]
        : null;
    const previousStage = currentStageIndex > 0 ? stages[currentStageIndex - 1] : null;
    const currentStage = currentStageIndex >= 0 ? stages[currentStageIndex] : null;

    const handleStatusUpdate = async (newStatus) => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(false);

            const response = await axios.put(
                `${rootUrl}/api/job-cards/${jobCard.id}`,
                { status: newStatus, updated_by: user?.id || user?.email },
                { headers: { Authorization: `Bearer ${user?.token}` } }
            );

            if (response.data.success) {
                setSuccess(true);
                if (onStatusUpdate) {
                    onStatusUpdate();
                }
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setError(response.data.message || 'Failed to update status');
            }
        } catch (err) {
            console.error('Error updating status:', err);
            setError(err.response?.data?.message || 'Failed to update status');
        } finally {
            setLoading(false);
        }
    };

    if (stagesLoading) {
        return (
            <Card className="p-6">
                <Typography className="text-gray-600">Loading stages...</Typography>
            </Card>
        );
    }

    if (stagesError) {
        return (
            <Card className="p-6">
                <Alert color="red">Error loading stages: {stagesError}</Alert>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {error && <Alert color="red">{error}</Alert>}
            {success && <Alert color="green">Status updated successfully</Alert>}

            {/* Header Section - Job Card & Status Only */}
            <Card className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] shadow-sm">
                <div className="p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
                        {/* Left: Job Card Number Only */}
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#FF5733] rounded-full flex items-center justify-center">
                                <FontAwesomeIcon icon={faTools} className="text-white text-xl" />
                            </div>
                            <Typography variant="h4" className="text-[#2a2e30] font-bold">
                                {jobCardNumber}
                            </Typography>
                        </div>

                        {/* Right: Status Chips */}
                        <div className="flex gap-2 flex-wrap">
                            <Chip
                                value={currentStage?.name || currentStatus}
                                size="lg"
                                className="font-bold text-white normal-case"
                                style={{ backgroundColor: currentStage?.color || '#9CA3AF' }}
                            />
                            {jobCard?.locked && (
                                <Chip
                                    value="LOCKED"
                                    icon={<FontAwesomeIcon icon={faLock} />}
                                    color="amber"
                                    className="font-semibold"
                                />
                            )}
                            {jobCard?.cancelled && (
                                <Chip
                                    value="CANCELLED"
                                    icon={<FontAwesomeIcon icon={faBan} />}
                                    color="red"
                                    className="font-semibold"
                                />
                            )}
                            {jobCard?.invoiced && (
                                <Chip
                                    value="INVOICED"
                                    icon={<FontAwesomeIcon icon={faFileInvoiceDollar} />}
                                    color="purple"
                                    className="font-semibold"
                                />
                            )}
                        </div>
                    </div>

                    {/* Status Progression */}
                    <div className="flex items-center gap-2 mb-6">
                        {stages.map((stage, index) => (
                            <React.Fragment key={stage.code}>
                                <div
                                    className="flex items-center justify-center w-10 h-10 rounded-full text-xs font-bold text-white transition-all"
                                    style={{
                                        backgroundColor: index <= currentStageIndex ? stage.color : '#D1D5DB',
                                        opacity: index <= currentStageIndex ? 1 : 0.4
                                    }}
                                    title={stage.name}
                                >
                                    {index <= currentStageIndex ? (
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-white" />
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                                {index < stages.length - 1 && (
                                    <div
                                        className="flex-1 h-2 rounded-full transition-all"
                                        style={{
                                            backgroundColor: index < currentStageIndex ? stages[index].color : '#D1D5DB',
                                            opacity: index < currentStageIndex ? 1 : 0.4
                                        }}
                                    ></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                        {previousStage && nextStage ? (
                            <Button
                                size="md"
                                style={{ backgroundColor: previousStage.color }}
                                onClick={() => handleStatusUpdate(previousStage.code)}
                                loading={loading}
                                disabled={loading || jobCard?.locked}
                                className="flex items-center justify-center gap-2 text-white px-6 py-3 normal-case text-sm"
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                                <span className="hidden sm:inline">Back to {previousStage.name}</span>
                                <span className="sm:hidden">Back</span>
                            </Button>
                        ) : (
                            <div></div>
                        )}

                        {!nextStage && !previousStage && currentStageIndex >= 0 && (
                            <Typography className="text-xs text-gray-600 italic text-center flex-1">
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-600" />
                                Final stage
                            </Typography>
                        )}
                        {!nextStage && !previousStage && currentStageIndex < 0 && (
                            <Typography className="text-xs text-red-600 italic text-center flex-1">
                                ⚠ Invalid status
                            </Typography>
                        )}

                        {nextStage ? (
                            <Button
                                size="md"
                                style={{ backgroundColor: nextStage.color }}
                                onClick={() => handleStatusUpdate(nextStage.code)}
                                loading={loading}
                                disabled={loading}
                                className="flex items-center justify-center gap-2 text-white px-6 py-3 normal-case text-sm"
                            >
                                <span className="hidden sm:inline">Move to {nextStage.name}</span>
                                <span className="sm:hidden">Next</span>
                                <ChevronRightIcon className="w-4 h-4" />
                            </Button>
                        ) : currentStageIndex >= 0 ? (
                            <div></div>
                        ) : null}
                    </div>
                </div>
            </Card>
        </div>
    );
}
